import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import dayjs from "dayjs";

import { Capacitor } from "@capacitor/core";
import { PrivacyScreen } from "@capacitor/privacy-screen";

import {
  Backdrop,
  Box,
  Card,
  CardMedia,
  Grid,
  IconButton,
  ImageListItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  IconCheck,
  IconEdit,
  IconStar,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { blueGrey, green, red } from "@mui/material/colors";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import TimeUtils from "utils/time";
import useCustomSnackbar from "hooks/customSnackbar";
import CurrentSession from "data/userSession";
import { useStorageImage } from "components/StorageImage";
import {
  useSfwReveal,
  TapToReveal,
  SfwHidden,
  SFW_BLUR_FILTER,
} from "components/BlurReveal";

function ImageTile({
  image,
  editable,
  onChange,
  onGraph = false,
  userAbove18 = null,
}) {
  const { enqueueSnackbar } = useCustomSnackbar();
  // Album photos load via the SDK (auth token attached); public URLs now 403.
  const imageUrl = useStorageImage(image.location);
  // SFW mode: blur the tile behind a tap-to-reveal. The overlay swallows the
  // first tap, so the lightbox only ever opens after reveal (always clear).
  const { blurred, reveal } = useSfwReveal();
  const [isEditing, setIsEditing] = useState(false);

  const [album, setAlbum] = useState(image.album);
  const [caption, setCaption] = useState(image.caption || "");
  const [date, setDate] = useState(dayjs(image.date));
  const [displayDate, setDisplayDate] = useState(
    moment(image.date).format(TimeUtils.FullDateString)
  );
  const [favorited, setFavorited] = useState(image.favorited);
  const [authMatches, setAuthMatches] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isOver18, setIsOver18] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const isOtherUsersImage =
    loggedIn && !CurrentSession.authenticationStrictlyMatches(image.uid);

  const openExpanded = () => {
    setExpanded(true);
  };

  const closeExpanded = () => {
    setExpanded(false);
  };

  // Enable privacy screen whenever another user's image is rendered
  useEffect(() => {
    if (Capacitor.isNativePlatform() && isOtherUsersImage) {
      PrivacyScreen.enable();
      return () => {
        PrivacyScreen.disable();
      };
    }
  }, [isOtherUsersImage]);

  useEffect(() => {
    CurrentSession.load().then(async (user) => {
      setAuthMatches(CurrentSession.authenticationStrictlyMatches(user.uid));
      setLoggedIn(CurrentSession.isSignedIn);
      // Check age verification - use prop if provided (for viewed users), otherwise use current user
      // If prop is false, that means we explicitly want to hide (viewed user or current user is <18)
      // If prop is true, both users are above18
      // If prop is null, use current user's status (backward compatibility)
      if (userAbove18 !== null) {
        setIsOver18(userAbove18);
      } else {
        setIsOver18(user.above18);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAbove18]);
  useEffect(() => {
    setDisplayDate(moment(image.date).format(TimeUtils.FullDateString));
    setCaption(image.caption);
  }, [image]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    image.save().catch((e) => {
      enqueueSnackbar(e.message, { variant: "error" });
    });
    onChange();
  };

  async function onDelete() {
    image
      .delete()
      .then(() => {
        enqueueSnackbar("Image deleted", { variant: "info" });
        onChange();
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: "error" });
      });
  }

  // Render action buttons for editing/deleting
  const renderActionButtons = () => {
    if (!editable || !authMatches) return null;

    return (
      <Grid
        container
        justifyContent="flex-end"
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
      >
        {isEditing && (
          <IconButton
            onClick={() => onDelete()}
            sx={{
              color: red[400],
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              mr: 1,
            }}
          >
            <IconTrash />
          </IconButton>
        )}
        <IconButton
          onClick={isEditing ? handleSave : handleEdit}
          sx={{
            color: isEditing ? green[500] : "inherit",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
          }}
        >
          {isEditing ? <IconCheck /> : <IconEdit />}
        </IconButton>
      </Grid>
    );
  };

  // Render the image or sign-in message
  const renderImageSection = () => {
    if (loggedIn) {
      return (
        <ImageListItem>
          <Box sx={{ position: "relative", width: 200, height: 200 }}>
            <CardMedia
              component="img"
              sx={{
                width: 200,
                height: 200,
                cursor: "pointer",
                filter: blurred ? SFW_BLUR_FILTER : undefined,
              }}
              image={imageUrl}
              alt={image.name}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onClick={openExpanded}
            />
            {blurred &&
              (onGraph ? <SfwHidden /> : <TapToReveal onReveal={reveal} />)}
          </Box>
        </ImageListItem>
      );
    }
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ width: 200, height: 200, backgroundColor: "grey.100" }}
      >
        <Typography>Sign in to view</Typography>
      </Grid>
    );
  };

  // Render content section (date, caption, favorite button)
  const renderContentSection = () => {
    if (isEditing) {
      return (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={date}
              onChange={(newDate) => {
                image.date = moment(newDate.toDate());
                setDisplayDate(image.date.format(TimeUtils.FullDateString));
                setDate(newDate);
              }}
              disableFuture
            />
          </LocalizationProvider>
          <TextField
            label="Album Name"
            value={album}
            onChange={(e) => {
              image.album = e.target.value;
              setAlbum(image.album);
            }}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            fullWidth
          />
          <TextField
            label="Caption"
            value={caption}
            onChange={(e) => {
              image.caption = e.target.value;
              setCaption(image.caption);
            }}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            multiline
            fullWidth
          />
        </>
      );
    }

    return (
      <>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="overline">{displayDate}</Typography>
          <Tooltip title="Show on Progress Page" arrow>
            <span>
              <IconButton
                disabled={!authMatches}
                onClick={
                  editable && authMatches
                    ? () => {
                        image.toggleFavorited();
                        setFavorited(image.favorited);
                        handleSave();
                      }
                    : null
                }
              >
                {favorited ? (
                  <IconStar color="#ffc107" fill="#ffc107" />
                ) : (
                  <IconStar color={blueGrey[100]} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
        {caption && (
          <Typography variant="caption" sx={{ pb: 1 }}>
            {caption}
          </Typography>
        )}
      </>
    );
  };

  // Return null if user is not over 18
  if (loggedIn && !isOver18) {
    return null;
  }

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          width: loggedIn ? "min-content" : 200,
          margin: onGraph ? 0 : 1,
          borderTopLeftRadius: onGraph ? 0 : 24,
          borderTopRightRadius: onGraph ? 0 : 24,
          position: "relative",
        }}
      >
        {/* Action buttons overlay */}
        {renderActionButtons()}

        {/* Image section */}
        {renderImageSection()}

        {/* Content section */}
        <Grid container sx={{ p: "0 10px", width: "100%" }}>
          {renderContentSection()}
        </Grid>
      </Card>

      {/* Expanded image lightbox - portaled to body to escape any stacking contexts */}
      {expanded &&
        createPortal(
          <Backdrop open onClick={closeExpanded} sx={{ zIndex: 9999 }}>
            <IconButton
              onClick={closeExpanded}
              sx={{
                position: "absolute",
                top: "calc(env(safe-area-inset-top, 16px) + 8px)",
                right: "calc(env(safe-area-inset-right, 16px) + 8px)",
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.6)" },
              }}
            >
              <IconX />
            </IconButton>
            <img
              src={imageUrl}
              alt={image.name}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                minWidth: "70vw",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Backdrop>,
          document.body
        )}
    </>
  );
}

export default ImageTile;
